import React, { useRef } from "react";
import { useServerApi } from "../hooks/userServerApi";
import _ from "lodash";

import {
  Timeline,
  Text,
  Avatar,
  TextInput,
  Group,
  ActionIcon,
  Tooltip,
  ScrollArea,
  List,
  Badge,
  Grid,
  Button,
} from "@mantine/core";
import {
  IconCircleCheck,
  IconCircleMinus,
  IconCircleX,
  IconExclamationMark,
  IconTrash,
} from "@tabler/icons";
import moment from "moment";
import ReactJson from "react-json-view";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { saveFormFields } from "../helpers/formFieldSave";
import { useActionRight } from "../hooks/useActionRight";

const cloneCases = (list) => (list || []).map((item) => ({ ...item }));

const saveErrorMessage = (result) => {
  if (result?.mcaLocked || result?.cancelled) return "";
  const error = result?.error;
  if (typeof error === "string" && error) return error;
  if (error?.msg) return String(error.msg);
  if (error?.message) return String(error.message);
  if (typeof result?.message === "string" && result.message) return result.message;
  return "Save failed";
};

const notifyPendingCaseSaveFailed = (result) => {
  if (result?.mcaLocked) {
    showNotification({
      color: "orange",
      icon: <IconExclamationMark size={18} />,
      title: "Changes blocked",
      message:
        "This record has a pending MCA approval. Use the MCA request to revise or cancel it.",
    });
    return;
  }
  const message = saveErrorMessage(result);
  if (!message) return;
  showNotification({
    color: "red",
    icon: <IconExclamationMark size={18} />,
    title: "Save Fail",
    message,
  });
};

const OrderPendingCase = ({
  form,
  name,
  title,
  disabled = false,
  readOnly = false,
  scrollHeight = 300,
  ...props
}) => {
  const [t] = useTranslation();
  const pendingcases = _.get(form.values, name) || [];
  const ref = useRef(null);
  const { isActionVisible, isActionEnabled } = useActionRight();
  const showInput = isActionVisible("order", "pendingCases");
  const locked = Boolean(disabled || readOnly || form?.isMcaLocked?.());
  const canEdit = isActionEnabled("order", "pendingCases") && !locked;

  const commitCases = async (next) => {
    const previous = cloneCases(pendingcases);
    const result = await saveFormFields(
      form,
      { [name]: next },
      { skipFormUpdate: true }
    );
    if (!result || result.success === false) {
      form.setFieldValue?.(name, previous);
      notifyPendingCaseSaveFailed(result);
      return result;
    }
    return result;
  };

  const handleKeyDown = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!canEdit) return;
    const description = e.currentTarget.value;
    if (!String(description ?? "").trim()) return;
    const result = await commitCases([
      ...cloneCases(pendingcases),
      {
        description,
        createdAt: moment().format("YYYY-MM-DD"),
        completed: false,
      },
    ]);
    if (result?.success && ref.current) ref.current.value = "";
  };

  const remove = (index) => {
    if (!canEdit) return;
    const next = cloneCases(pendingcases).filter((_, itemIndex) => itemIndex !== index);
    commitCases(next);
  };

  const completeItem = (index) => {
    if (locked) return;
    const next = cloneCases(pendingcases);
    if (!next[index]) return;
    next[index] = { ...next[index], completed: !next[index].completed };
    commitCases(next);
  };

  const incompleteCases = pendingcases.filter((c) => !c.completed);
  return (
    <div id="pendingSection">
      {/* <ReactJson src={pendingcases} style={{ background: "white" }} /> */}
      {showInput && (
        <TextInput
          ref={ref}
          mb={20}
          placeholder="Enter to add pending case"
          disabled={!canEdit}
          onKeyDown={handleKeyDown}
        />
      )}
      <Text size={"xs"} mb="xl">
        {`${incompleteCases.length} case${
          incompleteCases.length > 1 ? "s" : ""
        } ${incompleteCases.length > 1 ? "are" : "is"} pending.`}
      </Text>
      {!_.isEmpty(pendingcases) && (
        <>
          {pendingcases.map((c, index) => (
            <Grid key={c._id || index}>
              <Grid.Col span={9}>
                <Group noWrap>
                  <Badge
                    size="xs"
                    color={c.completed ? "green" : "red"}
                    radius={0}
                  >
                    {c.completed ? "DONE" : "PENDING"}
                  </Badge>
                  <Text size={"xs"} td={c.completed ? "line-through" : ""}>
                    {c?.createdAt
                      ? `[ ${moment(c?.createdAt).format("ll")} ]`
                      : ""}{" "}
                    {c?.description}
                  </Text>
                </Group>
              </Grid.Col>
              <Grid.Col span={2}>
                <Group spacing={2}>
                  {canEdit && (
                    <Tooltip label={t("Remove")} withArrow position="top">
                      <ActionIcon
                        mx={"sm"}
                        size={15}
                        color="dark"
                        aria-label={t("Remove")}
                        onClick={() => remove(index)}
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Tooltip>
                  )}

                  {!locked && (
                    <Tooltip
                      label={t(c.completed ? "Undo" : "Mark complete")}
                      withArrow
                      position="top"
                    >
                      <ActionIcon
                        mx={"sm"}
                        size={15}
                        color="dark"
                        aria-label={t(c.completed ? "Undo" : "Mark complete")}
                        onClick={() => completeItem(index)}
                      >
                        {!c.completed && <IconCircleCheck />}
                        {c.completed && <IconCircleX />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Grid.Col>
            </Grid>
          ))}
        </>
      )}
    </div>
  );
};

export default OrderPendingCase;
